import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Real testimonials - keys for translation
const testimonialKeys = ['mauro', 'laura', 'blerim'];

// Star rating component
const Stars = () => (
  <div className="flex justify-center gap-1 mb-6">
    {[...Array(5)].map((_, i) => (
      <span key={i} className="text-[#FFD700] text-xl">⭐</span>
    ))}
  </div>
);

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Get testimonials from translations
  const getTestimonials = () => {
    return testimonialKeys.map((key, index) => ({
      id: index + 1,
      key,
      name: t(`testimonials.reviews.${key}.name`),
      role: t(`testimonials.reviews.${key}.role`),
      location: t(`testimonials.reviews.${key}.location`),
      text: t(`testimonials.reviews.${key}.text`, { defaultValue: '' }) || null,
    }));
  };

  const testimonials = getTestimonials();

  // Auto-rotate testimonials
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonialKeys.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="py-32 bg-[#0a0a0a] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FFD700]/30 to-transparent" />
      
      {/* Large quote marks background */}
      <div className="absolute top-20 left-10 text-[20vw] font-serif text-white/[0.02] leading-none pointer-events-none select-none">
        "
      </div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header - Fixed for mobile */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-6 block">
            {t('testimonials.subtitle')}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9]">
            <span className="block sm:inline">{t('testimonials.title')}</span>
            <span className="text-outline block sm:inline sm:ml-4">{t('testimonials.title_outline')}</span>
          </h2>
          <p className="text-white/80 text-lg font-light max-w-xl mx-auto mt-8">
            {t('testimonials.description')}
          </p>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              {/* Stars */}
              <Stars />
              
              {/* Quote */}
              <div className="mb-8">
                {currentTestimonial.text ? (
                  <p className="font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white leading-relaxed italic px-4">
                    "{currentTestimonial.text}"
                  </p>
                ) : (
                  <div className="flex flex-col items-center gap-4 px-4">
                    <div className="flex items-center gap-2 text-[#FFD700]">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      <span className="font-display text-lg">{t('testimonials.verified_google')}</span>
                    </div>
                    <p className="font-serif text-xl sm:text-2xl md:text-3xl text-white/80 leading-relaxed">
                      {t('testimonials.satisfied_customer')}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Author */}
              <div className="flex flex-col items-center gap-3">
                {/* Avatar */}
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FFD700] to-[#B8860B] flex items-center justify-center">
                  <span className="font-display text-2xl text-black">
                    {currentTestimonial.name.charAt(0)}
                  </span>
                </div>
                
                <div>
                  <h4 className="font-display text-xl text-white">
                    {currentTestimonial.name}
                  </h4>
                  <p className="text-[#FFD700] text-sm">
                    {currentTestimonial.role}, {currentTestimonial.location}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation dots */}
          <div className="flex justify-center gap-3 mt-12">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-[#FFD700] w-8' 
                    : 'bg-white/20 hover:bg-white/40'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>

          {/* Arrow navigation - hidden on very small screens */}
          <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 hidden sm:flex justify-between pointer-events-none">
            <motion.button
              onClick={() => goToSlide((currentIndex - 1 + testimonials.length) % testimonials.length)}
              className="w-12 h-12 border border-white/20 flex items-center justify-center hover:border-[#FFD700] hover:bg-[#FFD700] transition-all duration-300 pointer-events-auto group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg className="w-5 h-5 text-white/60 group-hover:text-black transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
              </svg>
            </motion.button>
            
            <motion.button
              onClick={() => goToSlide((currentIndex + 1) % testimonials.length)}
              className="w-12 h-12 border border-white/20 flex items-center justify-center hover:border-[#FFD700] hover:bg-[#FFD700] transition-all duration-300 pointer-events-auto group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg className="w-5 h-5 text-white/60 group-hover:text-black transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
              </svg>
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
